import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DuePicker, TimeSelect, UserAvatar } from "@/components/TaskShared";
import { toast } from "sonner";
import { User, Users, CheckCheck, ArrowLeft, Sparkles } from "lucide-react";

// Shown after a task is completed: "What's the next move here?"
// Lets you spin up a follow-up task in the same project (for you or someone else), or dismiss.
export function NextMoveModal({ open, onClose, projectId, sourceTaskId = null, users = [], apiClient = api, onCreated }) {
  const [step, setStep] = useState("choose"); // choose | form
  const [mode, setMode] = useState("me"); // me | other
  const [title, setTitle] = useState("");
  const [assignee, setAssignee] = useState("");
  const [due, setDue] = useState("");
  const [start, setStart] = useState("");
  const [saving, setSaving] = useState(false);
  const meId = useRef(null);

  useEffect(() => {
    let live = true;
    apiClient.get("/auth/me").then(({ data }) => { if (live) meId.current = data?.user?.id || null; }).catch(() => {});
    return () => { live = false; };
  }, [apiClient]);

  useEffect(() => { if (open) { setStep("choose"); setMode("me"); setTitle(""); setAssignee(""); setDue(""); setStart(""); } }, [open]);

  const pickMe = () => { setMode("me"); setAssignee(meId.current || ""); setStep("form"); };
  const pickOther = () => { setMode("other"); setAssignee(""); setStep("form"); };

  const create = async () => {
    if (!title.trim()) { toast.error("Give the task a title."); return; }
    if (mode === "other" && !assignee) { toast.error("Choose who this task is for."); return; }
    const assignee_id = (mode === "me" ? (assignee || meId.current) : assignee) || null;
    setSaving(true);
    try {
      const { data } = await apiClient.post("/console/tasks", { project_id: projectId, title: title.trim(), assignee_id, due_date: due || null, start_time: start || null, connected_task_id: sourceTaskId || null });
      toast.success("Follow-up task created");
      onClose && onClose();
      onCreated && onCreated(data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
      <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C] max-w-md" data-testid="next-move-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#5b2fd6]" /> What's the next move here?</DialogTitle>
          <DialogDescription className="text-[#9FA1A4]">{step === "choose" ? "Nice work finishing that. Want to line up what comes next?" : "A quick follow-up task in the same project."}</DialogDescription>
        </DialogHeader>

        {step === "choose" ? (
          <div className="grid gap-2 py-1">
            <button data-testid="next-move-me" onClick={pickMe} className="flex items-center gap-3 rounded-xl border border-[#EEEFEF] bg-white px-4 py-3 text-left hover:border-[#d9ccff] hover:bg-[#F6F2FF] transition-colors">
              <span className="h-9 w-9 rounded-full bg-[#EDE7FF] flex items-center justify-center"><User className="h-4 w-4 text-[#5b2fd6]" /></span>
              <span className="font-medium text-sm">Task for me</span>
            </button>
            <button data-testid="next-move-other" onClick={pickOther} className="flex items-center gap-3 rounded-xl border border-[#EEEFEF] bg-white px-4 py-3 text-left hover:border-[#d9ccff] hover:bg-[#F6F2FF] transition-colors">
              <span className="h-9 w-9 rounded-full bg-[#EDE7FF] flex items-center justify-center"><Users className="h-4 w-4 text-[#5b2fd6]" /></span>
              <span className="font-medium text-sm">Task for someone else</span>
            </button>
            <button data-testid="next-move-none" onClick={() => onClose && onClose()} className="flex items-center gap-3 rounded-xl border border-transparent px-4 py-3 text-left hover:bg-[#F6F6FD] transition-colors">
              <span className="h-9 w-9 rounded-full bg-[#EAF9F1] flex items-center justify-center"><CheckCheck className="h-4 w-4 text-[#1f9d55]" /></span>
              <span className="font-medium text-sm text-[#595D62]">Nothing. All done!</span>
            </button>
          </div>
        ) : (
          <div className="grid gap-3 py-1">
            <div>
              <label className="text-xs text-[#9FA1A4] mb-1 block">Task title</label>
              <Input data-testid="next-move-title" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); create(); } }} placeholder="What needs doing next?" className="bg-[#F6F6FD] border-[#EEEFEF]" />
            </div>
            {mode === "other" ? (
              <div>
                <label className="text-xs text-[#9FA1A4] mb-1 block">Assign to</label>
                <Select value={assignee || "none"} onValueChange={(v) => setAssignee(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="next-move-assignee" className="bg-[#F6F6FD] border-[#EEEFEF]"><SelectValue placeholder="Choose a teammate" /></SelectTrigger>
                  <SelectContent className="bg-white border-[#EEEFEF] max-h-60"><SelectItem value="none">Choose a teammate</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-xs text-[#595D62]"><UserAvatar name={(users.find((u) => u.id === assignee) || {}).name || "You"} uid={assignee} size={22} /> Assigned to you</div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs text-[#9FA1A4] mb-1 block">Due date</label><DuePicker testid="next-move-due" value={due} onChange={setDue} /></div>
              <div><label className="text-xs text-[#9FA1A4] mb-1 block">Start time</label><TimeSelect testid="next-move-start" value={start} onChange={setStart} placeholder="No time" /></div>
            </div>
            <div className="flex items-center justify-between pt-1">
              <button data-testid="next-move-back" onClick={() => setStep("choose")} className="inline-flex items-center gap-1 text-xs text-[#9FA1A4] hover:text-[#5b2fd6]"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
              <Button data-testid="next-move-create" onClick={create} disabled={saving || !title.trim()} className="bg-[#06060C] text-white rounded-full">Create task</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
